import { ablyChannelNamesForSession } from "_shared/_stores/realtime/ably-channel-names";
import reduxAblyMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-ably-middleware";
import { RootState } from "_shared/_stores/root-reducer";
import { AppDispatch } from "_shared/_stores/store-types";
import Guid from "_shared/guids/guid";
import {
  removeTeamTemplateApiCaches,
  upsertTeamTemplateApiCaches,
} from "work/team-templates/_api/team-template-api";
import TeamTemplateSchema, { TeamTemplateJSON } from "work/team-templates/_schema/team-template-schema";
import { removeTeamTemplate, upsertTeamTemplate } from "work/team-templates/_store/team-templates-redux-slice";

const getTeamTemplateIdValue = (payload: unknown): string | undefined => {
  if (typeof payload === "string") return payload;
  if (payload && typeof payload === "object" && "id" in payload) {
    const id = (payload as { id?: unknown }).id;
    return typeof id === "string" ? id : undefined;
  }

  return undefined;
};

const isUnauthorizedPayload = (payload: unknown): payload is { id: string; isUnauthorized: true } => {
  if (!payload || typeof payload !== "object") return false;
  return (payload as { isUnauthorized?: unknown }).isUnauthorized === true;
};

const updateTeamTemplateByIdCache = ({
  dispatch,
  getState,
  teamTemplateJSON,
}: {
  dispatch: AppDispatch;
  getState: () => RootState;
  teamTemplateJSON: TeamTemplateJSON;
}) => {
  if (!teamTemplateJSON.id) {
    throw new Error("Received team template event with no team template ID");
  }

  upsertTeamTemplateApiCaches({ dispatch, getState, teamTemplateJSON });
  dispatch(upsertTeamTemplate(teamTemplateJSON));
};

const handleTeamTemplateRemoval = (payload: unknown) => (dispatch: AppDispatch, getState: () => RootState) => {
  const templateIdValue = getTeamTemplateIdValue(payload);
  if (!templateIdValue) {
    throw new Error("Received team-template-deleted event with no team template ID");
  }

  removeTeamTemplateApiCaches({ dispatch, getState, templateIdValue });
  dispatch(removeTeamTemplate(new Guid(templateIdValue)));
};

const handleTeamTemplateUpsert = (payload: unknown) => (dispatch: AppDispatch, getState: () => RootState) => {
  if (isUnauthorizedPayload(payload)) {
    handleTeamTemplateRemoval(payload)(dispatch, getState);
    return;
  }

  const teamTemplateJSON = TeamTemplateSchema.validateJSON(payload);
  updateTeamTemplateByIdCache({ dispatch, getState, teamTemplateJSON });
};

const callbacks = withCallbacks<RootState, AppDispatch>()
  .add("team-template-created", handleTeamTemplateUpsert)
  .add("team-template-updated", handleTeamTemplateUpsert)
  .add("team-template-deleted", handleTeamTemplateRemoval);

export const teamTemplatesAblyReduxMiddleware = reduxAblyMiddleware<RootState, AppDispatch>({
  callbacks,
  channelName: ablyChannelNamesForSession.teamTemplates,
  connectionHealth: { key: "teamTemplates", label: "Team Templates" },
  shouldConnectionStartImmediately: true,
});
